// Project card — grid + list variants with restriction lock.
import { Link } from "react-router-dom";
import { FolderKanban, Lock } from "lucide-react";
import type { Project } from "@/lib/api-types";
import { cn } from "@/lib/utils";
import { ProjectCardMenu } from "./project-card-menu";

type Props = {
  project: Project;
  to: string;
  view: "grid" | "list";
};

function formatDate(value: string | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function retentionLabel(days: Project["retentionDays"]): string {
  if (days === null || days === undefined) return "Retention off";
  return `${days}-day retention`;
}

/**
 * Small amber pill used to surface a project's Inactive state. Kept as a
 * shared component so grid + list views render the exact same badge.
 */
function InactiveBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400",
        className,
      )}
    >
      Inactive
    </span>
  );
}

/**
 * Restricted marker — locked 2026-07-14, migration 0011.
 * Visible to EVERYONE in the workspace so people know the project exists but
 * is gated. Rendered on both grid + list views. Not clickable itself; the
 * whole card's clickability is decided by the parent Wrapper below.
 */
function LockOverlay() {
  return (
    // Top-left overlay — moved 2026-07-14 after user flagged collision with the
    // three-dot menu at top-right. Nothing else lives at top-left on the grid
    // card, so the lock has room to breathe and reads clearly against any cover.
    <div
      className="pointer-events-none absolute left-2 top-2 grid h-6 w-6 place-items-center rounded-md bg-background/85 text-foreground shadow-sm ring-1 ring-border backdrop-blur"
      title="Restricted"
      aria-label="Restricted project"
    >
      <Lock className="h-3.5 w-3.5" strokeWidth={2} />
    </div>
  );
}

export function ProjectCard({ project, to, view }: Props) {
  const isInactive = !!project.inactiveAt;
  // Restriction gating — migration 0011. isProjectMember is TRUE for ADMIN /
  // FULL_ACCESS server-side, so this flag alone is enough to decide clickability.
  const isRestricted = !!project.restricted;
  const isLockedForMe = isRestricted && project.isProjectMember === false;

  // Common inner content for both views is a big blob, but the outer wrapper
  // switches between <Link> (accessible) and <div> (locked, non-navigable).
  // Using a discriminated Wrapper keeps a11y intact — a locked card must not
  // announce as a link.
  const wrapperClassName = (base: string) =>
    cn(
      base,
      isInactive && "opacity-70",
      isLockedForMe && "cursor-not-allowed opacity-60 hover:bg-card",
    );

  if (view === "list") {
    const inner = (
      <>
        <div className="relative grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg bg-brand-soft text-brand">
          {project.coverImageUrl ? (
            <img
              src={project.coverImageUrl}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <FolderKanban className="h-4 w-4" strokeWidth={1.75} />
          )}
          {isRestricted && (
            <span
              className="pointer-events-none absolute inset-0 grid place-items-center bg-black/25 text-white"
              title="Restricted"
              aria-label="Restricted project"
            >
              <Lock className="h-3 w-3" strokeWidth={2.25} />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="truncate font-medium text-foreground">{project.name}</div>
            {isInactive && <InactiveBadge />}
          </div>
        </div>
        <div className="shrink-0 text-xs text-muted-foreground">{retentionLabel(project.retentionDays)}</div>
        <div className="shrink-0 text-xs text-muted-foreground">{formatDate(project.createdAt)}</div>
        <ProjectCardMenu project={project} className="shrink-0" />
      </>
    );

    if (isLockedForMe) {
      return (
        <div
          className={wrapperClassName(
            "flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3",
          )}
          title="Restricted — ask an admin for access"
          aria-disabled="true"
        >
          {inner}
        </div>
      );
    }

    return (
      <Link
        to={to}
        className={wrapperClassName(
          "flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3 transition-colors hover:bg-secondary/40",
        )}
      >
        {inner}
      </Link>
    );
  }

  const gridInner = (
    <>
      <ProjectCardMenu project={project} className="absolute right-3 top-3 z-10" />

      <div className="relative grid aspect-video place-items-center overflow-hidden rounded-lg bg-secondary/60">
        {project.coverImageUrl ? (
          <img
            src={project.coverImageUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <FolderKanban className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
        )}
        {isRestricted && <LockOverlay />}
      </div>
      <div>
        <div className="truncate font-medium text-foreground">{project.name}</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>{retentionLabel(project.retentionDays)}</span>
          <span>·</span>
          <span>{formatDate(project.createdAt)}</span>
          {/* Sit on the same meta row so the badge lines up with the retention
              chip the user pointed at in the screenshot — bottom-right of the
              card metadata area. */}
          {isInactive && (
            <>
              <span className="ml-auto" />
              <InactiveBadge />
            </>
          )}
        </div>
      </div>
    </>
  );

  if (isLockedForMe) {
    return (
      <div
        className={wrapperClassName(
          "group relative flex flex-col gap-3 rounded-xl border border-border bg-card p-4",
        )}
        title="Restricted — ask an admin for access"
        aria-disabled="true"
      >
        {gridInner}
      </div>
    );
  }

  return (
    <Link
      to={to}
      className={wrapperClassName(
        "group relative flex flex-col gap-3 rounded-xl border border-border bg-card p-4 transition-colors hover:bg-secondary/40",
      )}
    >
      {gridInner}
    </Link>
  );
}
